import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Search, ShieldCheck, X } from "lucide-react";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllEmployeesNoPageNoFilter } from "../../../api/hrm/employees/employeeInfo";
import { getAllBranches_Array, getBranchById } from "../../../api/hrm/branches/info";
import {
  getUserRoles,
  createUserRole,
  deleteUserRole,
  getUserDataScopes,
  updateUserDataScopes,
} from "../../../api/settings/user_roles";
import { getRoles } from "../../../api/settings/roleManager";
import { getRolePermissions } from "../../../api/settings/permissionManager";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

const ADVANCE_SCOPE_MODULE = "finance.advance.access";
const ADVANCE_PERMISSION_PREFIX = "finance.advance";
const CRUD_ACTIONS = ["C", "R", "U", "D", "A"];

const scopeOptions = [
  { value: "self", label: "Tự tạo" },
  { value: "department", label: "Bộ phận" },
  { value: "branch", label: "Chi nhánh" },
];

const emptyCrud = { C: false, R: false, U: false, D: false, A: false };

const normalizeCrudKey = (crudMap) =>
  CRUD_ACTIONS.filter((x) => Boolean(crudMap?.[x]))
    .sort()
    .join("|");

const AdvancePermissionModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [roleProfiles, setRoleProfiles] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [currentAffiliationType, setCurrentAffiliationType] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  const currentUser = useMemo(() => {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const currentBranchId = currentUser?.branch?.id || "";
  const roleNames = useMemo(
    () =>
      (Array.isArray(currentUser?.roles) ? currentUser.roles : [])
        .map((r) => String(r || "").toLowerCase().trim()),
    [currentUser]
  );
  const isSuperAdminByRole =
    roleNames.includes("super admin") || roleNames.includes("superadmin");
  const isSuperAdminByPermission =
    hasPermission("settings.roles", "R") &&
    hasPermission("settings.role_permissions", "R");
  const isSuperAdmin = isSuperAdminByRole || isSuperAdminByPermission;
  const showBranchSelector =
    isSuperAdmin && currentAffiliationType !== "franchise";
  const effectiveBranchId = showBranchSelector ? selectedBranchId : currentBranchId;

  const roleByCrudKey = useMemo(() => {
    const m = new Map();
    roleProfiles.forEach((rp) => {
      const key = normalizeCrudKey(rp.crud);
      if (key && !m.has(key)) m.set(key, rp);
    });
    return m;
  }, [roleProfiles]);

  const filteredRows = useMemo(() => {
    const kw = debouncedKeyword.trim().toLowerCase();
    if (!kw) return tableRows;
    return tableRows.filter((row) => {
      const employeeName = String(row.employee_name || "").toLowerCase();
      const departmentName = String(row.department_name || "").toLowerCase();
      return employeeName.includes(kw) || departmentName.includes(kw);
    });
  }, [tableRows, debouncedKeyword]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    if (!isOpen || !currentBranchId) return;
    getBranchById(currentBranchId)
      .then((res) =>
        setCurrentAffiliationType(String(res?.affiliation_type || "").toLowerCase())
      )
      .catch(() => setCurrentAffiliationType(""));
  }, [isOpen, currentBranchId]);

  useEffect(() => {
    if (!isOpen || !showBranchSelector) {
      setBranchOptions([]);
      setSelectedBranchId("");
      return;
    }
    const loadBranchOptions = async () => {
      try {
        const branches = await getAllBranches_Array();
        const options = (branches || [])
          .filter((b) => {
            const type = String(b.affiliation_type || "").toLowerCase();
            return type === "hq" || type === "affiliated";
          })
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "vi"))
          .map((b) => ({ value: b.id, label: b.name }));
        setBranchOptions(options);
        if (options.length > 0) {
          const currentExists = options.some((x) => x.value === currentBranchId);
          setSelectedBranchId(currentExists ? currentBranchId : options[0].value);
        }
      } catch (err) {
        setBranchOptions([]);
        setSelectedBranchId("");
      }
    };
    loadBranchOptions();
  }, [isOpen, showBranchSelector, currentBranchId]);

  const renderHighlightedText = (text, keyword) => {
    const source = String(text || "");
    const kw = String(keyword || "").trim();
    if (!kw) return source;

    const escapedKw = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedKw})`, "gi");
    const parts = source.split(regex);

    return parts.map((part, idx) => {
      if (part.toLowerCase() === kw.toLowerCase()) {
        return (
          <mark
            key={`${part}-${idx}`}
            className="bg-amber-100 text-amber-800 px-0.5 rounded"
          >
            {part}
          </mark>
        );
      }
      return <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>;
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const [employeesRes, userRolesRes, rolesRes] = await Promise.all([
          getAllEmployeesNoPageNoFilter(),
          getUserRoles({ page: 1, pageSize: 5000 }),
          getRoles(),
        ]);

        const allEmployees = employeesRes?.items || [];
        const branchEmployees = allEmployees
          .filter((e) => e?.is_active === true || e?.is_active === "true" || e?.is_active === 1)
          .filter((e) => !effectiveBranchId || e.branch_id === effectiveBranchId)
          .sort((a, b) =>
            String(a.employee_name || "").localeCompare(String(b.employee_name || ""), "vi")
          );
        setEmployees(branchEmployees);

        const assigned = userRolesRes?.data || [];
        setUserRoles(assigned);

        const rawRoles = rolesRes?.data || [];
        const rolePermissionResults = await Promise.allSettled(
          rawRoles.map((role) => getRolePermissions(role.id))
        );

        const profiles = [];
        rawRoles.forEach((role, idx) => {
          const result = rolePermissionResults[idx];
          if (result.status !== "fulfilled") return;
          const permissions = result.value?.data || [];
          const advancePermissions = permissions.filter((p) =>
            String(p.code || "").startsWith(ADVANCE_PERMISSION_PREFIX)
          );
          if (!advancePermissions.length) return;

          const crud = { ...emptyCrud };
          advancePermissions.forEach((p) => {
            (p.actions || []).forEach((a) => {
              if (CRUD_ACTIONS.includes(a)) crud[a] = true;
            });
          });
          profiles.push({ id: role.id, name: role.name, crud });
        });
        setRoleProfiles(profiles);

        const rowsWithScope = await Promise.all(
          branchEmployees.map(async (emp) => {
            const employeeRoles = assigned.filter((x) => x.employee_id === emp.id);
            const advanceRole = employeeRoles.find((x) =>
              profiles.some((p) => p.id === x.role_id)
            );
            const roleProfile = profiles.find((p) => p.id === advanceRole?.role_id);
            let scopeType = "self";
            try {
              const scopeRes = await getUserDataScopes(emp.id);
              const scopeItem = (scopeRes?.scopes || []).find(
                (x) => x.module_code === ADVANCE_SCOPE_MODULE
              );
              if (scopeItem?.scope_type) scopeType = scopeItem.scope_type;
            } catch {
              scopeType = "self";
            }
            return {
              employee_id: emp.id,
              employee_name: emp.employee_name || emp.name || "Không tên",
              department_name: emp.department_name || "—",
              branch_id: emp.branch_id || null,
              department_id: emp.department_id || null,
              current_role_id: advanceRole?.role_id || "",
              crud: roleProfile?.crud || { ...emptyCrud },
              scope_type: scopeType,
            };
          })
        );

        setTableRows(rowsWithScope);
      } catch (err) {
        console.error(err);
        showErrorToast("Không thể tải dữ liệu phân quyền tạm ứng.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [isOpen, effectiveBranchId]);

  const updateRow = (employeeId, updater) => {
    setTableRows((prev) =>
      prev.map((row) =>
        row.employee_id === employeeId ? { ...row, ...updater(row) } : row
      )
    );
  };

  const toggleCrud = (employeeId, action) => {
    updateRow(employeeId, (row) => ({
      crud: {
        ...row.crud,
        [action]: !row.crud[action],
      },
    }));
  };

  const toggleCrudRow = (employeeId, checked) => {
    updateRow(employeeId, () => ({
      crud: {
        C: checked,
        R: checked,
        U: checked,
        D: checked,
        A: checked,
      },
    }));
  };

  const updateScope = (employeeId, scopeType) => {
    updateRow(employeeId, () => ({ scope_type: scopeType }));
  };

  const resolveRoleByCrud = (crudMap) => {
    const exact = roleByCrudKey.get(normalizeCrudKey(crudMap));
    if (exact) return exact;
    const selected = CRUD_ACTIONS.filter((k) => crudMap[k]);
    if (!selected.length) return null;

    // Ưu tiên role bao phủ toàn bộ quyền đã chọn.
    const superset = roleProfiles.filter((rp) =>
      selected.every((action) => rp.crud[action])
    );
    if (superset.length > 0) {
      return superset.sort((a, b) => {
        const aExtra = CRUD_ACTIONS.filter((k) => a.crud[k] && !selected.includes(k)).length;
        const bExtra = CRUD_ACTIONS.filter((k) => b.crud[k] && !selected.includes(k)).length;
        return aExtra - bExtra;
      })[0];
    }

    // Fallback: chọn role gần nhất theo số action trùng (dựa trên quyền hiện có).
    const scored = roleProfiles
      .map((rp) => {
        const matchCount = selected.filter((action) => rp.crud[action]).length;
        return { rp, matchCount };
      })
      .filter((x) => x.matchCount > 0)
      .sort((a, b) => b.matchCount - a.matchCount);
    return scored[0]?.rp || null;
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const skippedRows = [];
      for (const row of tableRows) {
        const employeeAssignedRoles = userRoles.filter(
          (x) => x.employee_id === row.employee_id
        );
        const currentAdvanceRole = employeeAssignedRoles.find((x) =>
          roleProfiles.some((p) => p.id === x.role_id)
        );

        const nextRole = resolveRoleByCrud(row.crud);
        const selectedCrud = CRUD_ACTIONS.filter((k) => row.crud[k]);
        if (!nextRole && selectedCrud.length > 0) {
          skippedRows.push(row.employee_name);
          continue;
        }

        if (currentAdvanceRole?.role_id && currentAdvanceRole.role_id !== nextRole?.id) {
          await deleteUserRole(row.employee_id, currentAdvanceRole.role_id);
        }
        if (nextRole?.id && currentAdvanceRole?.role_id !== nextRole.id) {
          await createUserRole({
            employee_id: row.employee_id,
            role_id: nextRole.id,
          });
        }

        const scopeRes = await getUserDataScopes(row.employee_id);
        const existingScopes = scopeRes?.scopes || [];
        const mergedScopes = existingScopes.filter(
          (x) => x.module_code !== ADVANCE_SCOPE_MODULE
        );
        const scopeValue =
          row.scope_type === "branch"
            ? row.branch_id
            : row.scope_type === "department"
            ? row.department_id
            : null;
        mergedScopes.push({
          module_code: ADVANCE_SCOPE_MODULE,
          scope_type: row.scope_type,
          scope_value: scopeValue,
        });
        await updateUserDataScopes(row.employee_id, mergedScopes);
      }

      if (skippedRows.length > 0) {
        showSuccessToast(
          `Đã lưu an toàn. Bỏ qua ${skippedRows.length} nhân sự chưa có role tạm ứng phù hợp CRUD.`
        );
      } else {
        showSuccessToast("Đã lưu phân quyền tạm ứng theo bảng.");
      }
      onClose?.();
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Không thể lưu phân quyền."
      );
    } finally {
      setSaving(false);
    }
  };

  const hasConfigurableAdvanceRoles = roleProfiles.length > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[220] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-[2rem] w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-100 shadow-2xl flex flex-col"
        >
          <div className="sticky top-0 z-20 bg-white border-b border-gray-100">
            <div className="px-7 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-red-700 text-white">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-gray-800 uppercase tracking-tight">
                    Phân quyền tạm ứng theo chi nhánh
                  </h3>
                  <p className="text-[11px] uppercase text-gray-500 font-bold tracking-widest">
                    Dạng bảng: chọn C/R/U/D/A và Data Scope theo từng nhân sự
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-7 pb-4">
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                {showBranchSelector && (
                  <div className="w-full md:w-72">
                    <Select
                      options={branchOptions}
                      value={branchOptions.find((x) => x.value === selectedBranchId) || null}
                      onChange={(opt) => setSelectedBranchId(opt?.value || "")}
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Chọn chi nhánh..."
                    />
                  </div>
                )}
                <div className="relative w-full max-w-md">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder="Tìm nhanh tên nhân viên / bộ phận..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
                  />
                </div>
              </div>
              {!hasConfigurableAdvanceRoles && (
                <p className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Chưa tìm thấy role có quyền liên quan module tạm ứng trong hệ thống.
                </p>
              )}
            </div>
          </div>

          <div className="p-6 flex-1 overflow-hidden">
            {loading ? (
              <div className="py-12 flex items-center justify-center text-gray-500 gap-2">
                <Loader2 size={18} className="animate-spin" />
                Đang tải danh sách nhân sự...
              </div>
            ) : (
              <div className="h-full overflow-x-auto">
                <table className="w-full min-w-[1060px] border-collapse">
                  <colgroup>
                    <col style={{ width: "56px" }} />
                    <col />
                    <col style={{ width: "72px" }} />
                    <col style={{ width: "72px" }} />
                    <col style={{ width: "72px" }} />
                    <col style={{ width: "72px" }} />
                    <col style={{ width: "72px" }} />
                    <col style={{ width: "240px" }} />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-14">
                        Chọn
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100">
                        Nhân sự
                      </th>
                      <th className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-20">
                        C
                      </th>
                      <th className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-20">
                        R
                      </th>
                      <th className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-20">
                        U
                      </th>
                      <th className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-20">
                        D
                      </th>
                      <th
                        className="text-center px-3 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100 w-20"
                        title="Approve / Reject / Settlement"
                      >
                        A
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-black uppercase text-gray-500 border-y border-gray-100">
                        Data Scope
                      </th>
                    </tr>
                  </thead>
                </table>

                <div className="max-h-[52vh] overflow-y-auto">
                  <table className="w-full min-w-[1060px] border-collapse">
                    <colgroup>
                      <col style={{ width: "56px" }} />
                      <col />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "240px" }} />
                    </colgroup>
                    <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.employee_id} className="odd:bg-white even:bg-gray-50/50">
                        <td className="px-3 py-3 text-center border-b border-gray-100">
                          <input
                            type="checkbox"
                            checked={CRUD_ACTIONS.every((action) => Boolean(row.crud[action]))}
                            onChange={(e) => toggleCrudRow(row.employee_id, e.target.checked)}
                            className="w-4 h-4 accent-red-600"
                          />
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-bold text-gray-800">
                            {renderHighlightedText(row.employee_name, debouncedKeyword)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {renderHighlightedText(row.department_name, debouncedKeyword)}
                          </div>
                        </td>
                        {CRUD_ACTIONS.map((action) => (
                          <td
                            key={`${row.employee_id}-${action}`}
                            className="px-3 py-3 text-center border-b border-gray-100"
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(row.crud[action])}
                              onChange={() => toggleCrud(row.employee_id, action)}
                              className="w-4 h-4 accent-red-600"
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 border-b border-gray-100">
                          <Select
                            options={scopeOptions}
                            value={scopeOptions.find((x) => x.value === row.scope_type) || null}
                            onChange={(opt) => updateScope(row.employee_id, opt?.value || "self")}
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                          />
                        </td>
                      </tr>
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-10 text-center text-sm font-medium text-gray-400"
                        >
                          Không có dữ liệu phù hợp.
                        </td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-100"
            >
              Đóng
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={loading || saving || tableRows.length === 0 || !hasConfigurableAdvanceRoles}
              className="px-5 py-2.5 rounded-xl bg-red-700 text-white font-bold hover:bg-red-800 disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              Lưu phân quyền
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdvancePermissionModal;
